import { Slot } from '@radix-ui/react-slot';
import {
  type ReactNode,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { css, styled } from 'styled-components';

type CollapsibleRootProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  id?: string;
  children: ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>;

type CollapsibleContextValue = {
  open: boolean;
  disabled?: boolean;
  contentId: string;
  toggle: () => void;
};

const CollapsibleContext = createContext<CollapsibleContextValue | null>(null);

function useCollapsible() {
  const ctx = useContext(CollapsibleContext);
  if (!ctx) throw new Error('Collapsible components must be used within <Collapsible.Root>');
  return ctx;
}

function CollapsibleRoot(props: CollapsibleRootProps) {
  const { open: openProp, defaultOpen, onOpenChange, disabled, id, children, ...divProps } = props;
  const isControlled = openProp !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(!!defaultOpen);
  const open = isControlled ? !!openProp : uncontrolledOpen;

  const contentId = useId();

  const toggle = useCallback(() => {
    if (disabled) return;
    const next = !open;
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  }, [disabled, open, isControlled, onOpenChange]);

  return (
    <CollapsibleRootStyled
      $open={isControlled ? openProp : uncontrolledOpen}
      data-collapsible=''
      data-state={open ? 'open' : 'closed'}
      {...divProps}
    >
      <CollapsibleContext.Provider value={{ open, disabled, contentId, toggle }}>
        {children}
      </CollapsibleContext.Provider>
    </CollapsibleRootStyled>
  );
}

const CollapsibleRootStyled = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: min-content ${props => (props.$open ? '1fr' : '0fr')};
  transition: grid-template-rows 300ms;
`;

const CollapsibleTrigger = forwardRef<
  HTMLButtonElement,
  (
    | ({ asChild?: false } & React.ButtonHTMLAttributes<HTMLButtonElement>)
    | ({ asChild: true } & React.ComponentPropsWithoutRef<typeof Slot>)
  ) & { asChild?: boolean }
>(function CollapsibleTrigger({ asChild, onClick, ...props }, ref) {
  const { open, disabled, contentId, toggle } = useCollapsible();
  const Comp: any = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : 'button'}
      aria-controls={contentId}
      aria-expanded={open}
      data-state={open ? 'open' : 'closed'}
      disabled={disabled || (props as any).disabled}
      onClick={(e: React.MouseEvent) => {
        // Slot merges handlers; child’s handler runs first per Radix pattern
        onClick?.(e as any);
        if (e.defaultPrevented) return;
        toggle();
      }}
      {...props}
    />
  );
});

const CollapsibleContent = forwardRef<
  HTMLDivElement,
  (
    | ({ asChild?: false } & React.HTMLAttributes<HTMLDivElement>)
    | ({ asChild: true } & React.ComponentPropsWithoutRef<typeof Slot>)
  ) & { asChild?: boolean }
>(function CollapsibleContent({ asChild, hidden, style, ...props }, ref) {
  const { open, contentId } = useCollapsible();
  const Comp: any = asChild ? Slot : 'div';

  return (
    <div aria-hidden={!open} style={{ overflow: 'hidden' }}>
      <Comp
        //as={Comp}
        ref={ref}
        id={contentId}
        data-state={open ? 'open' : 'closed'}
        style={{ ...style }}
        {...props}
      />
    </div>
  );
});

export const FuuzeCollapsible = {
  Root: CollapsibleRoot,
  Trigger: CollapsibleTrigger,
  Content: CollapsibleContent,
};
